import { format } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';

interface ImpersonationBannerProps {
  className?: string;
}

export const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({
  className,
}) => {
  const { user } = useOptionalSession();
  const { t } = useTranslation('translation', { keyPrefix: 'impersonation' });

  if (!user?.impersonator || !user?.impersonatedAt) {
    return null;
  }

  const isAdmin =
    user.impersonator.roles?.includes('admin') || user.impersonator.admin;
  const sessionDuration = isAdmin ? 24 : 1; // 24 hours for admin, 1 hour for support
  const expirationTime = new Date(
    (user.impersonatedAt + sessionDuration * 3600) * 1000
  );
  const formattedTime = format(expirationTime, "MMM d, yyyy 'at' h:mm a");

  const impersonatorName =
    user.impersonator.firstName && user.impersonator.lastName
      ? `${user.impersonator.firstName} ${user.impersonator.lastName}`
      : user.impersonator.nickname ||
        user.impersonator.email ||
        t('unknownUser');

  const currentUserName =
    user.firstName || user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email || user.nickname || t('unknownUser');

  return (
    <Box className={className} sx={{ width: '100%', zIndex: 1300 }}>
      <Alert severity="warning" sx={{ borderRadius: 0 }}>
        <Typography variant="body2">
          <Trans
            i18nKey="impersonation.message"
            values={{
              impersonatorName,
              currentUserName,
              formattedTime,
            }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Alert>
    </Box>
  );
};
